import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORIES, getSubcats } from "@/lib/categories";
import { toast } from "sonner";
import {
  Search, RotateCw, ArrowUp, ArrowDown, Trash2, Link2, Upload, ChevronLeft,
  Sparkles, FlipHorizontal, FlipVertical, Plus, X, Crop, Zap, Image as ImageIcon,
  Undo2, Redo2, ZoomIn, ZoomOut, Crosshair, Save, Settings
} from "lucide-react";
import ManualEditModal from "@/components/ManualEditModal";
import ProductCardWithVariants from "@/components/ProductCardWithVariants";
import BackdropPopover from "@/components/BackdropPopover";
import { extractPalette } from "@/lib/extractPalette";

const baseBtn = "inline-flex items-center justify-center gap-1 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] border transition-colors";

export default function Editor() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { id } = useParams();

  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("interior");  // Phase-4: Home is the default tab
  const [sub, setSub] = useState("all");
  const [mineMode, setMineMode] = useState(false); // "Mine" tab — curator's own pieces only
  // Admin · inline thumbnail editor for sub-cats. Holds the sub being edited
  // and the URL input. Only renders when the signed-in user is an admin.
  const [thumbEditSub, setThumbEditSub] = useState(null);
  const [thumbEditUrl, setThumbEditUrl] = useState("");
  const [thumbSaving, setThumbSaving] = useState(false);
  const [taxonomy, setTaxonomy] = useState({ categories: [], subcategories: {} });
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [title, setTitle] = useState("Untitled set");
  const [description, setDescription] = useState("");
  const [setCategory, setSetCategory] = useState("interior");
  const [tags, setTags] = useState("");
  const [clipUrl, setClipUrl] = useState("");
  const [clipCat, setClipCat] = useState("interior");
  const [clipSub, setClipSub] = useState("");
  const [clipping, setClipping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);
  const [bgQuality, setBgQuality] = useState(() => localStorage.getItem("vs_bg_quality") || "quality"); // "quality" | "fast"
  const [editingItem, setEditingItem] = useState(null); // canvas item under Manual Edit
  const [backdropUrl, setBackdropUrl] = useState("");
  const [backdropOpen, setBackdropOpen] = useState(false);
  // Phase-2 palette extraction state (under-canvas swatch row).
  const [palette, setPalette] = useState([]);          // string[] of 5 hex colours
  const [palettePending, setPalettePending] = useState(false);
  const [paletteAttached, setPaletteAttached] = useState(false); // surface on published Set?
  // Upload dialog state — file(s) + category + sub-category picked by the curator
  const [uploadFiles, setUploadFiles] = useState([]); // multi-file
  const [uploadCat, setUploadCat] = useState("interior");
  const [uploadSub, setUploadSub] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Variants of the currently selected canvas item — surfaces a "Community edits" rail
  const [selectedVariants, setSelectedVariants] = useState([]);
  // Phase-4 · "Choose Your Light" starting state. Defaults to whatever the
  // curator picked last session so returning users skip the prompt; new
  // curators see the 3-tile lighting picker before the canvas opens.
  const [chosenLight, setChosenLight] = useState(() => sessionStorage.getItem("vs_chosen_light") || "");
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const dragState = useRef(null);

  // Fetch live taxonomy from DB. Re-fetches on window-focus so renaming a
  // category in Admin (or adding/removing one) updates the Studio's panel
  // labels the moment the curator tabs back — no manual refresh needed.
  useEffect(() => {
    const load = () =>
      api.get("/categories").then(({ data }) => setTaxonomy(data)).catch(() => {});
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, []);

  const subOptions = cat !== "all" ? (taxonomy.subcategories?.[cat] || getSubcats(cat)) : [];
  const topCats = taxonomy.categories?.length ? taxonomy.categories : CATEGORIES;
  useEffect(() => { setSub("all"); }, [cat]);

  // Load existing
  useEffect(() => {
    if (!id) return;
    api.get(`/sets/${id}`).then(({ data }) => {
      setTitle(data.title); setDescription(data.description || "");
      setSetCategory(data.category || "interior");
      setTags((data.tags || []).join(", "));
      setBackdropUrl(data.backdrop_url || "");
      setPalette(Array.isArray(data.palette) ? data.palette : []);
      setPaletteAttached(Boolean(data.palette_attached));
      setItems((data.items || []).map((it, i) => ({ ...it, id: `it-${i}-${Math.random().toString(36).slice(2, 8)}` })));
    }).catch(() => {});
  }, [id]);

  const loadProducts = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (mineMode) {
      params.set("mine", "true");
    } else {
      if (cat !== "all") params.set("category", cat);
      if (sub !== "all" && sub !== "__all__") params.set("subcategory", sub);
    }
    params.set("limit", "200");
    const { data } = await api.get(`/products?${params.toString()}`);
    setProducts(data);
  };
  useEffect(() => { const t = setTimeout(loadProducts, 200); return () => clearTimeout(t); /* eslint-disable-line */ }, [q, cat, sub, mineMode]);

  // delete via key
  useEffect(() => {
    const h = (e) => {
      if (!selectedId) return;
      if (document.activeElement?.tagName === "INPUT" || document.activeElement?.tagName === "TEXTAREA") return;
      if (e.key === "Delete" || e.key === "Backspace") {
        setItems((p) => p.filter((i) => i.id !== selectedId)); setSelectedId(null);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [selectedId]);

  // Variants of the currently selected canvas item — surfaces a "Community edits" rail
  useEffect(() => {
    const selectedItem = items.find((i) => i.id === selectedId);
    if (!selectedItem?.product_id) { setSelectedVariants([]); return; }
    api.get(`/products/${selectedItem.product_id}/variants?sort=most_used`)
      .then(({ data }) => setSelectedVariants(Array.isArray(data) ? data : []))
      .catch(() => setSelectedVariants([]));
  }, [selectedId, items]);

  const newItem = (p, x, y, variant = null) => ({
    id: `it-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    product_id: p.id,
    image_url: variant ? variant.image_url : p.image_url,
    variant_id: variant?.id || "",
    variant_author_username: variant?.author_username || "",
    variant_kind: variant?.kind || "",
    category: p.category || "", subcategory: p.subcategory || "",
    x, y, width: 180, height: 180, rotation: 0,
    flipped_x: false, flipped_y: false,
    z_index: items.length + 1,
  });

  const addProduct = (p, variant = null) => {
    const it = newItem(p, 80 + Math.random() * 60, 60 + Math.random() * 40, variant);
    setItems((prev) => [...prev, it]); setSelectedId(it.id);
  };

  const onCanvasDrop = (e) => {
    e.preventDefault();
    const data = e.dataTransfer.getData("application/moodly-product");
    if (!data) return;
    const p = JSON.parse(data);
    const rect = canvasRef.current.getBoundingClientRect();
    const it = newItem(p, e.clientX - rect.left - 90, e.clientY - rect.top - 90);
    setItems((prev) => [...prev, it]); setSelectedId(it.id);
  };

  const onItemDragStart = (e, item) => {
    e.stopPropagation();
    setSelectedId(item.id);
    const rect = canvasRef.current.getBoundingClientRect();
    dragState.current = { mode: "move", id: item.id, ox: e.clientX - rect.left - item.x, oy: e.clientY - rect.top - item.y };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const onResizeStart = (e, item) => {
    e.stopPropagation();
    dragState.current = { mode: "resize", id: item.id, sx: e.clientX, sy: e.clientY, sw: item.width, sh: item.height };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const onMove = (e) => {
    const ds = dragState.current; if (!ds) return;
    const rect = canvasRef.current.getBoundingClientRect();
    if (ds.mode === "move") {
      setItems((p) => p.map((i) => i.id === ds.id ? { ...i, x: e.clientX - rect.left - ds.ox, y: e.clientY - rect.top - ds.oy } : i));
    } else {
      const d = Math.max(e.clientX - ds.sx, e.clientY - ds.sy);
      setItems((p) => p.map((i) => i.id === ds.id ? { ...i, width: Math.max(40, ds.sw + d), height: Math.max(40, ds.sh + d) } : i));
    }
  };
  const onUp = () => {
    if (dragState.current) snapshot();
    dragState.current = null;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
  };

  const update = (itemId, patch) => setItems((p) => p.map((i) => i.id === itemId ? { ...i, ...patch } : i));
  const remove = (itemId) => { setItems((p) => p.filter((i) => i.id !== itemId)); if (selectedId === itemId) setSelectedId(null); };
  const front = (itemId) => update(itemId, { z_index: Math.max(...items.map((i) => i.z_index || 0)) + 1 });
  const back = (itemId) => update(itemId, { z_index: Math.min(...items.map((i) => i.z_index || 0)) - 1 });

  // ---- Phase-4 edge-toolbar: Undo / Redo / Zoom / Center / Save-draft ----
  // Lightweight in-session history. We push the current items array onto the
  // stack on every meaningful mutation (drop, drag-end, remove, transform).
  // Re-doing pops from a parallel forward stack.
  const historyRef = useRef([]);     // past snapshots
  const futureRef = useRef([]);      // redo snapshots
  const snapshot = () => {
    historyRef.current.push(JSON.stringify(items));
    if (historyRef.current.length > 40) historyRef.current.shift();
    futureRef.current = [];
  };
  const undo = () => {
    const prev = historyRef.current.pop();
    if (!prev) return;
    futureRef.current.push(JSON.stringify(items));
    try { setItems(JSON.parse(prev)); } catch {/* ignore */}
  };
  const redo = () => {
    const next = futureRef.current.pop();
    if (!next) return;
    historyRef.current.push(JSON.stringify(items));
    try { setItems(JSON.parse(next)); } catch {/* ignore */}
  };
  const [zoom, setZoom] = useState(1);
  const zoomIn  = () => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)));
  const recenterCanvas = () => {
    // Scroll the canvas wrapper so the 800×800 board sits centred.
    const wrap = canvasRef.current?.parentElement;
    if (!wrap) return;
    wrap.scrollTo({
      top: (wrap.scrollHeight - wrap.clientHeight) / 2,
      left: (wrap.scrollWidth - wrap.clientWidth) / 2,
      behavior: "smooth",
    });
  };
  const saveDraft = () => {
    // Persist the in-progress spread to localStorage so a refresh doesn't
    // wipe an unpublished composition. Works for both new + existing sets.
    try {
      const key = id ? `vs_draft_${id}` : "vs_draft_new";
      localStorage.setItem(key, JSON.stringify({
        title, tags, items, palette, palette_attached: paletteAttached,
        category: setCategory, backdrop_url: backdropUrl,
        saved_at: new Date().toISOString(),
      }));
      toast.success("Draft saved");
    } catch { toast.error("Could not save draft"); }
  };

  // Admin · save a new thumbnail for a sub-category from inside the Studio.
  // Hits the existing PATCH endpoint; refreshes taxonomy so the tile
  // updates immediately without a page reload.
  const saveSubThumbnail = async () => {
    if (!thumbEditSub) return;
    const url = thumbEditUrl.trim();
    if (!url || !url.startsWith("http")) { toast.error("Paste an image URL or upload a file"); return; }
    setThumbSaving(true);
    try {
      await api.patch(`/admin/categories/${thumbEditSub.id}`, { thumbnail_url: url });
      toast.success(`${thumbEditSub.label} thumbnail updated`);
      // Refetch taxonomy so the tile updates without a page reload.
      const { data } = await api.get("/categories");
      setTaxonomy({ categories: data.categories || [], subcategories: data.subcategories || {} });
      setThumbEditSub(null);
      setThumbEditUrl("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update thumbnail");
    } finally { setThumbSaving(false); }
  };

  // Admin · upload a thumbnail image straight from the curator's machine.
  // Routes through /api/images/upload (which validates type + 8MB cap) and
  // stores the returned URL in thumbEditUrl — the existing Save button then
  // commits it to the category.
  const uploadSubThumbnail = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files (PNG / JPG / WEBP)"); return; }
    setThumbSaving(true);
    const t0 = Date.now();
    toast.loading("Uploading…", { id: "thumb-upload" });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/images/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // /api/images/upload returns a path that's relative to the app's base.
      // We resolve to a full absolute URL so /admin/categories validates it.
      const abs = data.image_url.startsWith("http")
        ? data.image_url
        : `${process.env.REACT_APP_BACKEND_URL}${data.image_url}`;
      setThumbEditUrl(abs);
      toast.success(`Uploaded in ${((Date.now() - t0) / 1000).toFixed(1)}s — preview below, then Save.`, { id: "thumb-upload" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed", { id: "thumb-upload" });
    } finally { setThumbSaving(false); }
  };

  const removeBg = async (item) => {
    if (!item) return;
    setRemovingBg(true);
    const t0 = Date.now();
    toast.loading(`Removing background${bgQuality === "fast" ? " (fast)" : ""}…`, { id: "rmbg" });
    try {
      const { data } = await api.post("/images/remove-bg", { image_url: item.image_url, quality: bgQuality });
      update(item.id, { image_url: data.image_url });
      toast.success(`Background removed in ${((Date.now() - t0) / 1000).toFixed(1)}s`, { id: "rmbg" });
    } catch {
      toast.error("Could not remove background", { id: "rmbg" });
    } finally { setRemovingBg(false); }
  };
  const toggleBgQuality = () => {
    const next = bgQuality === "fast" ? "quality" : "fast";
    setBgQuality(next);
    localStorage.setItem("vs_bg_quality", next);
    toast(`AI mode: ${next === "fast" ? "Fast (~3s, basic)" : "Quality (~7s, robust)"}`, { duration: 2200 });
  };

  const clipFromUrl = async () => {
    if (!clipUrl) return;
    setClipping(true);
    try {
      const { data } = await api.post("/products/clip", { url: clipUrl, category: clipCat, subcategory: clipSub });
      toast.success(`Clipped: ${data.title}`); setClipUrl("");
      loadProducts(); addProduct(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not clip URL";
      toast.error(msg);
    }
    finally { setClipping(false); }
  };

  const onUpload = async () => {
    if (!uploadFiles.length) { toast.error("Pick at least one file"); return; }
    setUploading(true);
    const fd = new FormData();
    uploadFiles.forEach((f) => fd.append("files", f));
    fd.append("category", uploadCat || "interior");
    if (uploadSub) fd.append("subcategory", uploadSub);
    try {
      const { data } = await api.post("/products/upload-batch", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const created = data.created || [];
      const errs = data.errors || [];
      if (errs.length) toast.error(`${errs.length} file(s) failed: ${errs.map(e => e.filename).join(", ")}`);
      if (created.length) toast.success(`${created.length} item${created.length === 1 ? "" : "s"} saved to ${uploadCat}${uploadSub ? ` · ${uploadSub}` : ""}`);
      // Jump the panel to the chapter that just received the upload so the
      // curator sees their new items immediately — fixes the "I uploaded
      // to Lighting but nothing shows up" perception when they were
      // standing on the Browse grid at upload time.
      setCat(uploadCat || "interior");
      if (uploadSub) setSub(uploadSub);
      setMineMode(false);
      loadProducts();
      // Drop the FIRST item onto the canvas as a quick affordance.
      if (created[0]) addProduct(created[0]);
      setUploadOpen(false);
      setUploadFiles([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (items.length === 0) { toast.error("Gather at least one piece"); return; }
    setSaving(true);
    try {
      const cover = items[0]?.image_url;
      // Pick the most common subcategory across canvas items as the set's tag
      const subCounts = {};
      for (const it of items) {
        const s = it.subcategory || "";
        if (s) subCounts[s] = (subCounts[s] || 0) + 1;
      }
      const dominantSub = Object.entries(subCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
      const payload = {
        title, description, cover_image: cover, category: setCategory,
        subcategory: dominantSub,
        backdrop_url: backdropUrl || "",
        palette: palette || [],
        palette_attached: paletteAttached,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        items: items.map(({ id: _, ...rest }) => rest),
      };
      const { data } = await api.post("/sets", payload);
      toast.success("Set published");
      nav(`/sets/${data.id}`);
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2EDE3]">
        <div className="text-center bg-white border border-[#EFEFEF] p-10">
          <h2 className="font-heading text-3xl">Sign in to start a set</h2>
          <button onClick={() => nav("/login")} className="mt-5 px-5 py-2.5 bg-[#333333] text-white font-mono text-[11px] uppercase tracking-[0.18em]">Sign in</button>
        </div>
      </div>
    );
  }

  const selected = items.find((i) => i.id === selectedId);

  return (
    <div className="h-screen w-full flex flex-col bg-[#F2EDE3]">
      {/* Top bar — Title / Tags / Backdrop / Publish */}
      <div
        data-testid="editor-top-bar"
        className="h-12 border-b border-[#DDD4C2] bg-white flex items-center px-4 gap-3 shrink-0"
      >
        <button data-testid="editor-back" onClick={() => nav("/")} className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.18em] hover:text-[#9CA47E]">
          <ChevronLeft className="w-3.5 h-3.5" /> Exit
        </button>
        <div className="font-heading text-lg ml-1">VoraStyle</div>
        <div className="h-5 w-px bg-[#E5E5E5] mx-1" />
        <Input
          data-testid="set-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="rounded-none border-[#E5E5E5] focus-visible:ring-0 focus-visible:border-[#333333] max-w-xs h-9 font-body"
          placeholder="Untitled set"
        />
        <select data-testid="set-category" value={setCategory} onChange={(e) => setSetCategory(e.target.value)} className="h-9 border border-[#E5E5E5] px-2 font-mono text-[11px] uppercase tracking-[0.15em] bg-white">
          <option value="interior">Home</option>
          <option value="design_elements">Design Elements</option>
        </select>
        <Input
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="tags, comma, separated"
          className="rounded-none border-[#E5E5E5] focus-visible:ring-0 max-w-xs h-9 font-mono text-xs"
          data-testid="set-tags"
        />
        <div className="ml-auto flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#595959] hidden md:inline">{items.length} pieces</span>
          <button
            data-testid="open-backdrop"
            type="button"
            onClick={() => setBackdropOpen(o => !o)}
            className={`px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] border cursor-pointer transition-colors ${backdropUrl ? "bg-[#9CA47E] text-white border-[#9CA47E] hover:bg-[#7E876A]" : "bg-white text-[#333333] border-[#333333] hover:bg-[#333333] hover:text-white"}`}
            title="AI backdrop"
          >
            <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Backdrop</span>
          </button>
          <button
            data-testid="editor-save"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 bg-[#9CA47E] hover:bg-[#7E876A] text-white font-mono text-[11px] uppercase tracking-[0.18em] disabled:opacity-50"
          >
            {saving ? "Publishing…" : "Publish set"}
          </button>
        </div>
      </div>

      {/* Workspace — flex-row-reverse puts the existing right-side <aside>
          on the LEFT visually, without moving JSX nodes. */}
      <div className="flex-1 flex flex-row-reverse overflow-hidden">
        {/* Canvas area */}
        <div
          className="flex-1 relative overflow-auto"
          style={{
            backgroundImage: "radial-gradient(#E5E5E5 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        >
          {/* Edge toolbar — thin vertical strip pinned to the canvas zone.
              Holds canvas-tool icons only: Undo · Redo · Zoom · Center · Save
              draft. Title/Tags/Backdrop/Publish remain in the top bar. */}
          <div
            data-testid="edge-toolbar"
            className="absolute left-3 top-3 z-20 flex flex-col items-center gap-1 bg-white/90 backdrop-blur-sm border border-[#DDD4C2] py-2 px-1 shadow-[0_1px_3px_rgba(40,30,20,0.04)]"
          >
            <EdgeBtn testid="tool-undo" onClick={undo} disabled={historyRef.current.length === 0} title="Undo (⌘Z)">
              <Undo2 className="w-4 h-4" />
            </EdgeBtn>
            <EdgeBtn testid="tool-redo" onClick={redo} disabled={futureRef.current.length === 0} title="Redo (⌘⇧Z)">
              <Redo2 className="w-4 h-4" />
            </EdgeBtn>
            <div className="h-px w-6 bg-[#DDD4C2] my-1" />
            <EdgeBtn testid="tool-zoom-in"  onClick={zoomIn}  title="Zoom in"><ZoomIn  className="w-4 h-4" /></EdgeBtn>
            <EdgeBtn testid="tool-zoom-out" onClick={zoomOut} title="Zoom out"><ZoomOut className="w-4 h-4" /></EdgeBtn>
            <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-[#8A847B] my-1" data-testid="tool-zoom-level">
              {Math.round(zoom * 100)}%
            </div>
            <div className="h-px w-6 bg-[#DDD4C2] my-1" />
            <EdgeBtn testid="tool-center" onClick={recenterCanvas} title="Center canvas">
              <Crosshair className="w-4 h-4" />
            </EdgeBtn>
            <EdgeBtn testid="tool-save-draft" onClick={saveDraft} title="Save draft">
              <Save className="w-4 h-4" />
            </EdgeBtn>
          </div>
          <div
            ref={canvasRef}
            key={backdropUrl || "no-backdrop"}
            data-testid="canvas-area"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onCanvasDrop}
            onClick={() => setSelectedId(null)}
            className="relative mx-auto my-8 bg-white shadow-[0_0_0_1px_#E5E5E5] transition-opacity duration-300"
            style={(() => {
              const base = {
                width: 800, height: 800,
                transform: `scale(${zoom})`,
                transformOrigin: "top center",
              };
              if (!backdropUrl) return { ...base, backgroundColor: "#FFFFFF" };
              if (backdropUrl.startsWith(":light/")) {
                const LIGHT_GRADIENTS = {
                  moody: "linear-gradient(135deg, #2F2A26 0%, #3D3934 60%, #5B524A 100%)",
                  linen: "linear-gradient(135deg, #F5F0E7 0%, #EFE7DA 60%, #E5DCC8 100%)",
                  amber: "linear-gradient(135deg, #C6916A 0%, #D9A77F 50%, #E9C39D 100%)",
                };
                const key = backdropUrl.slice(":light/".length);
                return {
                  ...base,
                  background: LIGHT_GRADIENTS[key] || "#FFFFFF",
                  backgroundColor: "transparent",
                };
              }
              return {
                ...base,
                backgroundImage: `url(${resolveImageUrl(backdropUrl)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundColor: "transparent",
              };
            })()}
          >
            {items.map((it) => {
              const sx = it.flipped_x ? -1 : 1;
              const sy = it.flipped_y ? -1 : 1;
              return (
                <div
                  key={it.id}
                  data-testid={`canvas-item-${it.id}`}
                  onMouseDown={(e) => onItemDragStart(e, it)}
                  onClick={(e) => { e.stopPropagation(); setSelectedId(it.id); }}
                  className={`absolute select-none ${selectedId === it.id ? "outline outline-[1.5px] outline-[#9CA47E]" : ""}`}
                  style={{
                    left: it.x, top: it.y, width: it.width, height: it.height,
                    transform: `rotate(${it.rotation}deg) scale(${sx}, ${sy})`,
                    zIndex: it.z_index, cursor: "move",
                  }}
                >
                  <img
                    src={resolveImageUrl(it.image_url)}
                    alt=""
                    draggable={false}
                    className="w-full h-full object-contain pointer-events-none"
                  />
                  {it.variant_author_username && (
                    <div
                      data-testid={`attribution-chip-${it.id}`}
                      title={`Edited by @${it.variant_author_username}${it.variant_kind ? ` · ${it.variant_kind}` : ""}`}
                      className="absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-1.5 py-[2px] bg-white border border-[#9CA47E] font-mono text-[8px] uppercase tracking-[0.2em] text-[#9CA47E] whitespace-nowrap pointer-events-none shadow-sm"
                      style={{ transform: `translateX(-50%) scale(${sx}, ${sy})` }}
                    >
                      <span className="text-[#9CA47E]">◇</span>
                      <span className="text-[#333333]">@{it.variant_author_username}</span>
                    </div>
                  )}
                  {selectedId === it.id && (
                    <div
                      onMouseDown={(e) => onResizeStart(e, it)}
                      className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-[#9CA47E] cursor-se-resize"
                      style={{ transform: `scale(${sx}, ${sy})` }}
                    />
                  )}
                </div>
              );
            })}

            {items.length === 0 && !chosenLight && !id && !backdropUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-[#F2EDE3]/85 backdrop-blur-[1px]" data-testid="choose-your-light">
                <div className="text-center px-8 max-w-2xl relative">
                  <button
                    type="button"
                    data-testid="light-dismiss"
                    onClick={() => { sessionStorage.setItem("vs_chosen_light", "blank"); setChosenLight("blank"); }}
                    aria-label="Dismiss"
                    className="absolute -top-2 right-0 w-7 h-7 inline-flex items-center justify-center text-[#5C5C5C] hover:text-[#333333] cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#9CA47E]">◇ A New Spread</div>
                  <h2 className="font-heading text-4xl mt-3 text-[#333333] italic leading-tight">Choose your light.</h2>
                  <p className="font-body text-sm text-[#595959] mt-3 max-w-md mx-auto leading-relaxed">
                    Every spread begins with a mood. Pick the atmosphere your room is in — or skip and start on a blank spread.
                  </p>
                  <div className="grid grid-cols-3 gap-3 mt-8" data-testid="light-options">
                    {[
                      { key: "moody",  label: "Moody",  caption: "Deep, low-lit",       css: "linear-gradient(135deg, #2F2A26 0%, #3D3934 60%, #5B524A 100%)",  fg: "#F2EDE3" },
                      { key: "linen",  label: "Linen",  caption: "Bright, gentle",      css: "linear-gradient(135deg, #F5F0E7 0%, #EFE7DA 60%, #E5DCC8 100%)",  fg: "#333333" },
                      { key: "amber",  label: "Amber",  caption: "Warm, golden hour",   css: "linear-gradient(135deg, #C6916A 0%, #D9A77F 50%, #E9C39D 100%)",  fg: "#332416" },
                    ].map((light) => (
                      <button
                        key={light.key}
                        type="button"
                        data-testid={`light-${light.key}`}
                        onClick={() => {
                          sessionStorage.setItem("vs_chosen_light", light.key);
                          setChosenLight(light.key);
                          setBackdropUrl(`:light/${light.key}`);
                        }}
                        className="group aspect-[3/4] flex flex-col items-start justify-end p-4 border border-[#CFC8B6] cursor-pointer transition-transform hover:scale-[1.02] hover:border-[#9CA47E]"
                        style={{ background: light.css, color: light.fg }}
                      >
                        <div className="font-heading text-2xl tracking-tight italic">{light.label}</div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.28em] opacity-80 mt-1">{light.caption}</div>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    data-testid="light-skip"
                    onClick={() => { sessionStorage.setItem("vs_chosen_light", "blank"); setChosenLight("blank"); }}
                    className="mt-7 font-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA47E] hover:text-[#7E876A] cursor-pointer underline-offset-4 hover:underline"
                  >
                    Skip · start on a blank spread
                  </button>
                </div>
              </div>
            )}

            {items.length === 0 && (chosenLight || id || backdropUrl) && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center px-8">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA47E]">◇ A Blank Spread</div>
                  <div className="font-heading text-3xl mt-3 text-[#333333] italic">Begin gathering.</div>
                  <div className="font-body text-sm text-[#595959] mt-3 max-w-xs mx-auto leading-relaxed">The Archive sits on the left — drag a piece, or tap one to set it down.</div>
                </div>
              </div>
            )}
          </div>

          {/* Palette strip — extracted from the composition on demand. Pure
              artistic feature: distills the canvas to a 5-colour bar. Curators
              can optionally Attach the palette to the published Set, where it
              renders as an elegant HEX-labelled strip below the canvas. */}
          <div className="px-6 pb-4 pt-3" data-testid="palette-strip-wrap">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9CA47E]">
                § Composition palette
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  data-testid="extract-palette"
                  disabled={palettePending || items.length === 0}
                  onClick={async () => {
                    setPalettePending(true);
                    try {
                      const p = await extractPalette(items, { count: 5 });
                      if (p.length === 0) { toast.error("Couldn't read the palette — try adding more items."); }
                      else setPalette(p);
                    } catch (e) { toast.error("Palette extraction failed."); }
                    finally { setPalettePending(false); }
                  }}
                  className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA47E] hover:text-[#7E876A] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer inline-flex items-center gap-1.5"
                >
                  <Sparkles className="w-3 h-3" />
                  {palettePending ? "Reading…" : palette.length ? "Re-extract" : "Extract palette"}
                </button>
                {palette.length > 0 && (
                  <button
                    type="button"
                    data-testid="palette-attach-toggle"
                    onClick={() => setPaletteAttached((v) => !v)}
                    aria-pressed={paletteAttached}
                    title={paletteAttached
                      ? "Palette will appear under the published board"
                      : "Pin this palette to the bottom of the published board"}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] border transition-colors cursor-pointer ${
                      paletteAttached
                        ? "bg-[#9CA47E] text-white border-[#9CA47E] hover:bg-[#7E876A]"
                        : "bg-white text-[#333333] border-[#333333] hover:bg-[#F2EDE3]"
                    }`}
                  >
                    {paletteAttached ? "✓ Attached to board" : "Attach to board"}
                  </button>
                )}
              </div>
            </div>
            {palette.length > 0 ? (
              <div data-testid="palette-strip">
                <div className="flex h-9 border border-[#E7E3DC]">
                  {palette.map((c, i) => (
                    <div key={i} className="flex-1" style={{ backgroundColor: c }} title={c} />
                  ))}
                </div>
                <div className="flex mt-1">
                  {palette.map((c, i) => (
                    <div key={i} className="flex-1 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-[#5C5C5C]">
                      {c}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-9 border border-dashed border-[#CFC8B6] flex items-center justify-center font-mono text-[9px] uppercase tracking-[0.22em] text-[#5C5C5C]">
                {items.length === 0 ? "Gather pieces to extract a palette" : "Tap Extract to read the composition"}
              </div>
            )}
          </div>

          {/* AI Backdrop popover (floats over the canvas area, top-right) */}
          <BackdropPopover
            open={backdropOpen}
            currentUrl={backdropUrl}
            onClose={() => setBackdropOpen(false)}
            onApply={(url) => setBackdropUrl(url)}
          />

          {/* Floating contextual toolbar */}
          {selected && (
            <div
              data-testid="floating-toolbar"
              className="absolute top-4 left-1/2 -translate-x-1/2 bg-white border border-[#333333] flex items-stretch shadow-sm z-20"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <ToolBtn testid="remove-bg" onClick={() => removeBg(selected)} disabled={removingBg} accent>
                <Sparkles className="w-3.5 h-3.5" /> {removingBg ? "Removing…" : "Remove BG"}
              </ToolBtn>
              <ToolBtn testid="bg-quality-toggle" onClick={toggleBgQuality} active={bgQuality === "fast"} title={bgQuality === "fast" ? "Fast mode (~3s)" : "Quality mode (~7s)"}>
                <Zap className="w-3.5 h-3.5" /> {bgQuality === "fast" ? "Fast" : "Quality"}
              </ToolBtn>
              <ToolBtn testid="manual-edit" onClick={() => setEditingItem(selected)}>
                <Crop className="w-3.5 h-3.5" /> Edit
              </ToolBtn>
              <ToolBtn testid="flip-h" onClick={() => update(selected.id, { flipped_x: !selected.flipped_x })} active={selected.flipped_x}>
                <FlipHorizontal className="w-3.5 h-3.5" /> Flip H
              </ToolBtn>
              <ToolBtn testid="flip-v" onClick={() => update(selected.id, { flipped_y: !selected.flipped_y })} active={selected.flipped_y}>
                <FlipVertical className="w-3.5 h-3.5" /> Flip V
              </ToolBtn>
              <ToolBtn testid="rotate-90" onClick={() => update(selected.id, { rotation: (selected.rotation + 15) % 360 })}>
                <RotateCw className="w-3.5 h-3.5" /> Rotate
              </ToolBtn>
              <ToolBtn testid="layer-up" onClick={() => front(selected.id)}>
                <ArrowUp className="w-3.5 h-3.5" /> Front
              </ToolBtn>
              <ToolBtn testid="layer-down" onClick={() => back(selected.id)}>
                <ArrowDown className="w-3.5 h-3.5" /> Back
              </ToolBtn>
              <ToolBtn testid="delete-item" onClick={() => remove(selected.id)} danger>
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </ToolBtn>
            </div>
          )}
        </div>

        {/* Items panel (Visual Category Grid). Visually sits on the LEFT
            because the workspace wrapper uses flex-row-reverse. */}
        <aside
          data-testid="library-panel"
          className="w-80 lg:w-[400px] border-r border-[#DDD4C2] bg-white flex flex-col shrink-0"
        >
          {/* ─── TABS ─────────────────────────────────────────────────
              Home · Design Elements · Mine. Switches the panel between
              the two top-level archives and the curator's own pieces.
              Replaces both the old breadcrumb and the giant hero cards. */}
          <div className="flex border-b border-[#DDD4C2] bg-white" data-testid="lib-tabs">
            {topCats.map((c) => {
              const active = cat === c.value;
              return (
                <button
                  key={c.value}
                  type="button"
                  data-testid={`lib-tab-${c.value}`}
                  onClick={() => { setCat(c.value); setSub("all"); setMineMode(false); }}
                  className={`flex-1 py-3.5 font-body text-[12px] tracking-[0.18em] uppercase transition-colors cursor-pointer border-b-2 ${
                    active && !mineMode
                      ? "text-[#2C2A26] border-[#9CA47E]"
                      : "text-[#8A847B] border-transparent hover:text-[#2C2A26]"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
            <button
              type="button"
              data-testid="lib-tab-mine"
              onClick={() => { setMineMode(true); setSub("all"); }}
              className={`flex-1 py-3.5 font-body text-[12px] tracking-[0.18em] uppercase transition-colors cursor-pointer border-b-2 ${
                mineMode
                  ? "text-[#2C2A26] border-[#9CA47E]"
                  : "text-[#8A847B] border-transparent hover:text-[#2C2A26]"
              }`}
            >
              Mine
            </button>
          </div>

          {/* ─── SEARCH BAR ─────────────────────────────────────────── */}
          <div className="px-4 pt-4 pb-3 border-b border-[#E5DECE] bg-[#F2EDE3]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8A847B]" />
              <Input
                data-testid="library-search"
                placeholder={
                  mineMode ? "Search your archive" :
                  cat === "all" ? "Search the whole archive" :
                  `Search ${topCats.find(c=>c.value===cat)?.label || cat}`
                }
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="rounded-none border-[#DDD4C2] focus-visible:ring-0 focus-visible:border-[#9CA47E] pl-9 h-10 bg-white font-body text-[13px]"
              />
            </div>
          </div>

          {/* ─── BODY ───────────────────────────────────────────────
              When a chapter is open (sub != all) OR there is an active
              search query OR Mine tab is on → show pieces grid.
              Otherwise → show the BROWSE thumbnail grid of chapters. */}
          {(sub !== "all" || q.trim() !== "" || mineMode) ? (
            <>
              {/* Breadcrumb when drilled in */}
              {!mineMode && (
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E5DECE] bg-white" data-testid="lib-breadcrumb">
                  <button
                    type="button"
                    data-testid="lib-back"
                    onClick={() => { setSub("all"); setQ(""); }}
                    className="inline-flex items-center gap-1 font-body text-[11px] tracking-[0.16em] uppercase text-[#6E6760] hover:text-[#2C2A26] cursor-pointer transition-colors"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    {q.trim() !== ""
                      ? "Clear search"
                      : `${topCats.find(c=>c.value===cat)?.label || ""} · ${(taxonomy.subcategories?.[cat] || []).find(s => s.value === sub)?.label || ""}`}
                  </button>
                  <span className="font-body text-[10px] tracking-[0.16em] uppercase text-[#8A847B]">
                    {products.length} {products.length === 1 ? "piece" : "pieces"}
                  </span>
                </div>
              )}

              <div className="overflow-y-auto p-3 grid grid-cols-2 gap-2 flex-1" data-testid="pieces-grid">
                {products.map((p) => (
                  <ProductCardWithVariants
                    key={p.id}
                    product={p}
                    onAdd={(prod, variant) => addProduct(prod, variant)}
                  />
                ))}
                {products.length === 0 && (
                  <div className="col-span-2 text-center py-10 font-body text-sm text-[#8A847B]">
                    <X className="w-5 h-5 mx-auto mb-2 opacity-50" />
                    No gathered pieces match.
                  </div>
                )}
              </div>
            </>
          ) : (
            // ─── BROWSE grid: 3-column thumbnail rail of chapters ───
            <div className="overflow-y-auto flex-1" data-testid="visual-category-grid">
              <div className="px-4 pt-4 pb-2">
                <div className="font-heading italic text-[15px] text-[#2C2A26] tracking-tight">
                  {cat === "interior" ? "Browse" : "Embellishments"}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2.5 px-4 pb-6">
                {(taxonomy.subcategories?.[cat] || []).map((s) => (
                  <div key={s.value} className="relative group/tile">
                    <button
                      type="button"
                      data-testid={`browse-sub-${s.value}`}
                      onClick={() => setSub(s.value)}
                      className="group w-full flex flex-col items-center gap-1.5 cursor-pointer"
                    >
                      <div className="w-full aspect-square overflow-hidden bg-white border border-[#DDD4C2] group-hover:border-[#9CA47E] transition-colors">
                        {s.thumbnail_url ? (
                          <img
                            src={resolveImageUrl(s.thumbnail_url)}
                            alt={s.label}
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-[#F2EDE3]">
                            <span className="font-heading italic text-2xl text-[#C9C0B3]">{s.label[0]}</span>
                          </div>
                        )}
                      </div>
                      <div className="font-body text-[10.5px] tracking-[0.14em] text-[#5C5650] group-hover:text-[#2C2A26] transition-colors text-center leading-tight">
                        {s.label}
                      </div>
                    </button>
                    {user?.role === "admin" && (
                      <button
                        type="button"
                        data-testid={`edit-thumb-${s.value}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setThumbEditSub(s);
                          setThumbEditUrl(s.thumbnail_url || "");
                        }}
                        title="Edit thumbnail"
                        className="absolute top-1.5 right-1.5 w-7 h-7 inline-flex items-center justify-center bg-white/95 border border-[#DDD4C2] text-[#5C5650] hover:text-[#9CA47E] hover:border-[#9CA47E] opacity-0 group-hover/tile:opacity-100 transition-opacity cursor-pointer"
                      >
                        <Settings className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── PINNED BOTTOM ACTIONS — Collect & Clip URL ─────────── */}
          <div className="border-t border-[#DDD4C2] bg-white p-3 flex gap-2 shrink-0">
            <button
              data-testid="open-upload"
              onClick={() => { setUploadOpen(true); setUploadCat(cat !== "all" ? cat : "interior"); setUploadSub(sub !== "all" && sub !== "__all__" ? sub : ""); }}
              className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 border border-[#2C2A26] bg-white hover:bg-[#2C2A26] hover:text-white font-body text-[11px] tracking-[0.18em] uppercase transition-colors cursor-pointer"
            >
              <Plus className="w-3 h-3" /> Collect
            </button>
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  data-testid="open-clip"
                  className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 border border-[#2C2A26] bg-white hover:bg-[#2C2A26] hover:text-white font-body text-[11px] tracking-[0.18em] uppercase transition-colors cursor-pointer"
                >
                  <Link2 className="w-3 h-3" /> Clip URL
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-[#DDD4C2]">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Clip from URL</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <Input data-testid="clip-url-input" placeholder="https://…/product" value={clipUrl} onChange={(e) => setClipUrl(e.target.value)} className="rounded-none border-[#DDD4C2] focus-visible:ring-0" />
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      data-testid="clip-url-category"
                      value={clipCat}
                      onChange={(e) => { setClipCat(e.target.value); setClipSub(""); }}
                      className="border border-[#DDD4C2] bg-white px-2 py-2 font-body text-[11px] tracking-[0.16em] uppercase cursor-pointer"
                    >
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <select
                      data-testid="clip-url-subcategory"
                      value={clipSub}
                      onChange={(e) => setClipSub(e.target.value)}
                      className="border border-[#DDD4C2] bg-white px-2 py-2 font-body text-[11px] tracking-[0.16em] uppercase cursor-pointer"
                    >
                      <option value="">— Chapter —</option>
                      {getSubcats(clipCat).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <button data-testid="clip-url-submit" onClick={clipFromUrl} disabled={clipping} className="w-full py-3 bg-[#2C2A26] text-white border border-[#2C2A26] hover:bg-[#1F1D1A] font-body text-[11px] tracking-[0.18em] uppercase cursor-pointer disabled:opacity-50">
                    {clipping ? "Clipping…" : "Clip product"}
                  </button>
                  <p className="text-[10px] font-body text-[#8A847B] leading-relaxed">For JS-only retailers (Talbots, Zara…), use the browser extension instead.</p>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Selected item — Community edits rail (shows when curator selects a canvas item with variants) */}
          {selectedId && selectedVariants.length > 0 && (
            <div className="p-3 border-t border-[#EFEFEF] bg-white" data-testid="selected-variants-rail">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#9CA47E]">◇ Community edits · {selectedVariants.length}</div>
                <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#A0A0A0]">tap to swap</span>
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {/* Original */}
                {(() => {
                  const sel = items.find((i) => i.id === selectedId);
                  const product = products.find((p) => p.id === sel?.product_id);
                  const origUrl = product?.image_url;
                  return origUrl ? (
                    <button
                      type="button"
                      data-testid="selected-variant-original"
                      onClick={() => update(selectedId, { image_url: origUrl, variant_id: "", variant_author_username: "", variant_kind: "" })}
                      className="shrink-0 w-14 h-14 border border-[#333333] bg-[#F2EDE3] cursor-pointer hover:ring-2 hover:ring-[#9CA47E] transition-all relative"
                      title="Original"
                    >
                      <img src={resolveImageUrl(origUrl)} alt="" className="w-full h-full object-cover" />
                      <span className="absolute bottom-0 left-0 right-0 bg-[#333333]/85 text-white font-mono text-[7px] uppercase tracking-widest px-1 py-[1px] text-center">orig</span>
                    </button>
                  ) : null;
                })()}
                {selectedVariants.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    data-testid={`selected-variant-${v.id}`}
                    onClick={() => update(selectedId, {
                      image_url: v.image_url,
                      variant_id: v.id,
                      variant_author_username: v.author_username || "",
                      variant_kind: v.kind || "",
                    })}
                    className="shrink-0 w-14 h-14 border border-[#E5E5E5] bg-white cursor-pointer hover:border-[#9CA47E] hover:ring-2 hover:ring-[#9CA47E] transition-all relative"
                    title={`@${v.author_username || "?"} · used ${v.used_count || 0} time${(v.used_count || 0) === 1 ? "" : "s"}${v.note ? ` — ${v.note}` : ""}`}
                  >
                    <img src={resolveImageUrl(v.image_url)} alt="" className="w-full h-full object-cover" />
                    {v.used_count > 0 && (
                      <span className="absolute top-0 right-0 bg-[#9CA47E] text-white font-mono text-[7px] uppercase tracking-widest px-1 py-[1px]">×{v.used_count}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Description footer */}
          <div className="p-3 border-t border-[#EFEFEF] bg-[#F2EDE3]">
            <label className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Description</label>
            <textarea
              data-testid="set-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full mt-1 border border-[#E5E5E5] focus:border-[#333333] focus:outline-none p-2 text-sm font-body bg-white"
              rows={2}
              placeholder="A short caption…"
            />
          </div>
        </aside>
      </div>

      {/* Manual Edit modal — crop / eraser / lasso */}
      <ManualEditModal
        open={!!editingItem}
        item={editingItem}
        productId={editingItem?.product_id}
        onClose={() => setEditingItem(null)}
        onApply={(payload) => {
          if (editingItem && payload?.image_url) {
            update(editingItem.id, { image_url: payload.image_url });
          }
        }}
      />

      {/* Upload-with-category dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => !o && setUploadOpen(false)}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-xl" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Collect to the Archive</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#595959] -mt-1">
              Pick a chapter once — every Collected Object lands in that shelf of the Archive, not just on this canvas.
            </p>
            <input
              data-testid="upload-input"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
              className="block w-full text-sm font-body file:mr-3 file:px-4 file:py-2 file:border file:border-[#333333] file:bg-white file:font-mono file:text-[10px] file:uppercase file:tracking-[0.18em] file:cursor-pointer file:hover:bg-[#333333] file:hover:text-white"
            />
            {uploadFiles.length > 0 && (
              <div className="border border-[#EFEFEF] bg-[#F2EDE3] p-2 max-h-44 overflow-y-auto" data-testid="upload-preview">
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#9CA47E] mb-1">◇ {uploadFiles.length} file{uploadFiles.length === 1 ? "" : "s"}</div>
                <div className="grid grid-cols-6 gap-1">
                  {uploadFiles.map((f, i) => (
                    <div key={i} className="relative group">
                      <div className="aspect-square bg-white border border-[#E5E5E5] overflow-hidden">
                        <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                      </div>
                      <button
                        type="button"
                        data-testid={`upload-remove-${i}`}
                        onClick={() => setUploadFiles(arr => arr.filter((_, idx) => idx !== i))}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-[#9CA47E] text-white text-[10px] leading-none rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        title="Remove"
                      >×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Archive</span>
                <select
                  data-testid="upload-category"
                  value={uploadCat}
                  onChange={(e) => { setUploadCat(e.target.value); setUploadSub(""); }}
                  className="w-full mt-1 border border-[#E5E5E5] bg-white px-2 py-2 font-mono text-[11px] uppercase tracking-[0.18em] cursor-pointer"
                >
                  {(topCats || CATEGORIES).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#595959]">Chapter</span>
                <select
                  data-testid="upload-subcategory"
                  value={uploadSub}
                  onChange={(e) => setUploadSub(e.target.value)}
                  className="w-full mt-1 border border-[#E5E5E5] bg-white px-2 py-2 font-mono text-[11px] uppercase tracking-[0.18em] cursor-pointer"
                >
                  <option value="">— None —</option>
                  {(taxonomy.subcategories?.[uploadCat] || getSubcats(uploadCat)).map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="upload-cancel" onClick={() => setUploadOpen(false)} className={`${baseBtn} border-[#E5E5E5] text-[#595959] hover:border-[#333333] hover:text-[#333333]`}>
                Cancel
              </button>
              <button type="button" data-testid="upload-submit" onClick={onUpload} disabled={!uploadFiles.length || uploading} className={`${baseBtn} bg-[#9CA47E] text-white border-[#9CA47E] hover:bg-[#7E876A]`}>
                {uploading ? "Collecting…" : `Collect ${uploadFiles.length || ""} to the Archive`}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin · inline thumbnail editor (Studio) */}
      <Dialog open={!!thumbEditSub} onOpenChange={(o) => { if (!o) { setThumbEditSub(null); setThumbEditUrl(""); } }}>
        <DialogContent className="rounded-none border-[#DDD4C2] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Edit thumbnail · <span className="italic">{thumbEditSub?.label}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="font-body text-[12px] text-[#5C5650] leading-relaxed">
              Upload an image from your computer <em>or</em> paste a direct image URL (right-click any image on Unsplash, Pinterest, your own site → <em>Copy image address</em>).
            </p>

            {/* Upload from computer */}
            <label
              data-testid="thumb-edit-upload-label"
              className="flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-[#9CA47E] text-[#9CA47E] hover:bg-[#F2EDE3] cursor-pointer font-body text-[11px] tracking-[0.18em] uppercase transition-colors"
            >
              <Upload className="w-4 h-4" />
              {thumbSaving ? "Uploading…" : "Upload from your computer"}
              <input
                data-testid="thumb-edit-upload-input"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="hidden"
                disabled={thumbSaving}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadSubThumbnail(f);
                  e.target.value = "";  // reset so the same file can be re-selected
                }}
              />
            </label>

            <div className="flex items-center gap-2">
              <div className="flex-1 h-px bg-[#DDD4C2]" />
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#8A847B]">or</span>
              <div className="flex-1 h-px bg-[#DDD4C2]" />
            </div>

            <Input
              data-testid="thumb-edit-url"
              placeholder="https://images.unsplash.com/photo-…"
              value={thumbEditUrl}
              onChange={(e) => setThumbEditUrl(e.target.value)}
              className="rounded-none border-[#DDD4C2] focus-visible:ring-0 font-body text-[13px]"
            />
            {thumbEditUrl && (thumbEditUrl.startsWith("http") || thumbEditUrl.startsWith("/")) && (
              <div className="aspect-square w-full border border-[#DDD4C2] bg-[#F2EDE3] overflow-hidden">
                <img src={thumbEditUrl} alt="preview" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                data-testid="thumb-edit-cancel"
                onClick={() => { setThumbEditSub(null); setThumbEditUrl(""); }}
                className="px-4 py-2.5 border border-[#DDD4C2] text-[#5C5650] hover:border-[#2C2A26] hover:text-[#2C2A26] font-body text-[11px] tracking-[0.18em] uppercase cursor-pointer transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="thumb-edit-save"
                onClick={saveSubThumbnail}
                disabled={thumbSaving || !thumbEditUrl}
                className="px-4 py-2.5 bg-[#9CA47E] text-white border border-[#9CA47E] hover:bg-[#7E876A] font-body text-[11px] tracking-[0.18em] uppercase cursor-pointer disabled:opacity-50"
              >
                {thumbSaving ? "Saving…" : "Save thumbnail"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ToolBtn({ children, onClick, disabled, testid, active, accent, danger }) {
  let cls = "px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] border-r border-[#333333]/15 last:border-r-0 inline-flex items-center gap-1.5 transition-colors ";
  if (danger) cls += "text-[#9CA47E] hover:bg-[#9CA47E] hover:text-white ";
  else if (accent) cls += "bg-[#9CA47E] text-white hover:bg-[#7E876A] ";
  else if (active) cls += "bg-[#333333] text-white ";
  else cls += "bg-white text-[#333333] hover:bg-[#F2EDE3] ";
  return (
    <button data-testid={testid} disabled={disabled} onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

// Edge-toolbar icon button — vertical, square, monochrome.
function EdgeBtn({ children, onClick, disabled, testid, title }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="w-8 h-8 inline-flex items-center justify-center text-[#5C5650] hover:bg-[#F5F0E7] hover:text-[#2C2A26] disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
    >
      {children}
    </button>
  );
}
